<script lang="ts">
import type { MaybeElementRef } from "@vueuse/core";
import type { LinkEditModel } from "./types";
import type { ShortcutJs } from "@/generated/models";
import { toTypedSchema } from "@vee-validate/valibot";
import { useFocus } from "@vueuse/core";
import { Form, FormControl, FormField, FormItem, useForm } from "ls/components/form";
import FormMessage from "ls/components/form/FormMessage.vue";
import TextField from "ls/components/form/TextField.vue";
import { GlobeIcon } from "lucide-vue-next";
import { hasProtocol, withProtocol } from "ufo";
import * as v from "valibot";
import { ref, useTemplateRef } from "vue";
import LinkSuggestions from "./LinkSuggestions.vue";
import { useSetLinkEditContextSubmitHandler } from "./useSetLinkEditContextSubmitHandler";

const Schema = v.object({
  url: v.pipe(
    v.string(),
    v.trim(),
  ),
  text: v.optional(v.string()),
});
</script>

<script setup lang="ts">
defineProps<{
  shortcut: string;
  filterFiles?: (file: ShortcutJs) => boolean;
}>();

const emits = defineEmits<{
  submit: [LinkEditModel];
  cancel: [];
}>();

const url = defineModel<string>("url", { required: true });
const text = defineModel<string>("text");

const urlField = useTemplateRef("urlField");
useFocus(urlField as MaybeElementRef, { initialValue: true });

const suggestionsRef = useTemplateRef("suggestions");
const suggestedItem = ref<string | undefined>();

const { handleSubmit } = useForm({
  validationSchema: toTypedSchema(Schema),
  initialValues: {
    url: url.value ?? "",
    text: text.value ?? "",
  },
});

const onSubmit = handleSubmit(values => {
  submitValues(values.url, values.text);
});

useSetLinkEditContextSubmitHandler(onSubmit);

function handleSuggestionSelect(url: string) {
  submitValues(url, text.value);
}

function submitValues(url: string, text?: string) {
  const urlValue = ensureProtocol(url.trim());
  if (!isValidUrl(urlValue)) return;
  emits("submit", {
    url: urlValue,
    text: text ?? null,
  });
}

function handleKeyDownEnter() {
  if (suggestedItem.value) {
    submitValues(suggestedItem.value, text.value);
    return;
  }
  onSubmit();
}

function ensureProtocol(value: string) {
  let url = value;
  if (!hasProtocol(url)) {
    url = withProtocol(url, "https://");
  }
  return url;
}

function isValidUrl(value: string) {
  if (!value) return false;
  return URL.canParse(value);
}
</script>

<template>
  <div
    class="flex min-h-0 w-full min-w-0 flex-col gap-2 p-1"
  >
    <div class="text-sm/6 font-semibold text-balance">
      Insert link
    </div>

    <div
      class="flex min-h-0 flex-col"
      @keydown.esc.prevent="emits('cancel')"
    >
      <Form class="flex min-h-0 flex-col gap-2" @submit="onSubmit">
        <FormField v-slot="{ componentField }" name="url">
          <FormItem class="flex flex-col">
            <FormControl>
              <TextField
                ref="urlField"
                v-bind="componentField"
                v-model="url"
                placeholder="Paste a URL or search by file name or ID"
                autocomplete="off"
                @keydown.down.prevent="suggestionsRef?.highlightNext()"
                @keydown.up.prevent="suggestionsRef?.highlightPrevious()"
                @keydown.enter.prevent="handleKeyDownEnter"
              />
              <slot name="error">
                <FormMessage />
              </slot>
            </FormControl>
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField }" name="text">
          <FormItem class="flex flex-col">
            <FormControl>
              <TextField
                v-bind="componentField"
                v-model="text"
                placeholder="Display text (optional)"
                autocomplete="off"
                @keydown.enter.prevent="handleKeyDownEnter"
              />
            </FormControl>
          </FormItem>
        </FormField>
        <div class="flex min-h-0 flex-col">
          <LinkSuggestions
            ref="suggestions"
            v-model="suggestedItem"
            :shortcut="shortcut"
            :search-term="url"
            @select="handleSuggestionSelect"
          >
            <template #recognized-url>
              <GlobeIcon class="size-4" aria-hidden="true" />
              <div class="overflow-hidden">
                Link to web page
              </div>
            </template>
          </LinkSuggestions>
        </div>
      </Form>
    </div>
  </div>
</template>
